import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MapPin, HelpCircle } from "lucide-react";
import { useEffect, useState } from "react";
import { PhotoUpload } from "@/components/profile/PhotoUpload";
import { ProfileAddressSection } from "@/components/profile/ProfileAddressSection";
import { geocodeAddress } from "@/utils/geocoding";
import { TagSelector } from "@/components/seller/meal-form/TagSelector";
import { useQuery } from "@tanstack/react-query";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useLocationServices } from "@/hooks/useLocationServices";
import { LocationButtonState } from "@/components/location/LocationButtonState";
import { Loader2 } from "lucide-react";

const AccountSettings = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const { 
    locationState, 
    requestLocation, 
    resetLocationServices,
    refreshLocation,
    isResetting,
    profileLoaded
  } = useLocationServices();

  const { data: foodTypes } = useQuery({
    queryKey: ['food-types'],
    queryFn: async () => {
      const { data } = await supabase.from('food_types').select('name').order('name');
      return data?.map(type => type.name) || [];
    }
  });

  const { data: chefTypes } = useQuery({
    queryKey: ['chef-types'],
    queryFn: async () => {
      const { data } = await supabase.from('chef_types').select('name').order('name');
      return data?.map(type => type.name) || [];
    }
  });

  const { data: allergens } = useQuery({
    queryKey: ['allergens'],
    queryFn: async () => {
      const { data } = await supabase.from('allergens').select('name').order('name');
      return data?.map(allergen => allergen.name) || [];
    }
  });

  const { data: dietaryRestrictions } = useQuery({
    queryKey: ['dietary-tags'],
    queryFn: async () => {
      const { data } = await supabase.from('dietary_tags').select('name').order('name');
      return data?.map(tag => tag.name) || [];
    }
  });

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      setUserEmail(user.email || '');
      const { data } = await supabase.from('profiles').select('*').eq('id', user.id).single();
      if (data) {
        setProfile(data);
      }
    };
    fetchProfile();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No user found');

      const coordinates = await geocodeAddress(profile.address, profile.city, profile.state, profile.postal_code);
      
      const { error } = await supabase.from('profiles').update({
        first_name: profile.first_name,
        last_name: profile.last_name,
        image_url: profile.image_url,
        address: profile.address,
        city: profile.city,
        state: profile.state,
        postal_code: profile.postal_code,
        food_preferences: profile.food_preferences || [],
        chef_preferences: profile.chef_preferences || [],
        allergy_considerations: profile.allergy_considerations || [],
        dietary_preferences: profile.dietary_preferences || [],
        ...(coordinates && {
          latitude: coordinates.latitude,
          longitude: coordinates.longitude
        })
      }).eq('id', user.id);

      if (error) throw error;
      
      if (coordinates && locationState.permission === 'granted') {
        try {
          await refreshLocation();
        } catch (refreshError) {
          console.error('Error refreshing location after profile update:', refreshError);
        }
      }

      toast({
        title: "Success",
        description: "Your profile has been updated.",
        duration: 1800
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive",
        duration: 1800
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLocationReset = () => {
    resetLocationServices();
  };

  const handleLocationUpdate = async () => {
    try {
      setIsLoading(true);
      
      if (locationState.permission === 'granted') {
        // If we already have permission, just refresh
        await refreshLocation();
        toast({
          title: "Location Updated",
          description: "Your location has been updated successfully.",
          duration: 1800
        });
      } else {
        // If we don't have permission, request it
        const result = await requestLocation();
        if (result) {
          toast({
            title: "Location Enabled",
            description: "Your location is now enabled and distances will be accurate.",
            duration: 1800
          });
        }
      }
    } catch (error) {
      console.error('Error updating location:', error);
      toast({
        title: "Error",
        description: "There was a problem updating your location.",
        variant: "destructive",
        duration: 1800
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getLocationStatusText = () => {
    if (!locationState.initialized) {
      return "Checking location permission...";
    }
    
    if (locationState.isLoading || locationState.permissionTransitioning) {
      return "Updating location status...";
    }
    
    if (isResetting) {
      return "Resetting location settings...";
    }
    
    // Check for inconsistent state first
    if (locationState.permission === 'granted' && !locationState.coords) {
      return 'Enabled - but location unavailable';
    }
    
    switch(locationState.permission) {
      case 'granted':
        return locationState.coords 
          ? `Enabled - location updated ${locationState.lastUpdated ? new Date(locationState.lastUpdated).toLocaleTimeString() : 'recently'}` 
          : 'Enabled - but location unavailable';
      case 'denied':
        return 'Disabled - enable in your browser settings';
      case 'prompt':
      default:
        return 'Not set - enable to see accurate distances';
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      <main className="flex-1 container mx-auto px-4 py-6">
        <Card className="p-6 mb-24">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={profile?.first_name || ''}
                  onChange={e => setProfile({ ...profile, first_name: e.target.value })}
                  placeholder="Enter your first name"
                  className="bg-[#EDEDED] border-gray-200 placeholder:text-[#6B6B6B]"
                />
              </div>

              <div>
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={profile?.last_name || ''}
                  onChange={e => setProfile({ ...profile, last_name: e.target.value })}
                  placeholder="Enter your last name"
                  className="bg-[#EDEDED] border-gray-200 placeholder:text-[#6B6B6B]"
                />
              </div>

              <PhotoUpload
                imageUrl={profile?.image_url}
                onPhotoUpdate={url => setProfile({ ...profile, image_url: url })}
              />

              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Label htmlFor="email">Email</Label>
                  <TooltipProvider delayDuration={100}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          className="p-0 h-4 w-4 text-muted-foreground hover:bg-transparent"
                          type="button"
                        >
                          <HelpCircle className="h-4 w-4" />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Email address cannot be changed here for security reasons. Please contact support if you need to update your email.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Input
                  id="email"
                  type="email"
                  disabled
                  value={userEmail}
                  className="bg-muted"
                />
              </div>

              <ProfileAddressSection
                address={profile?.address || ''}
                city={profile?.city || ''}
                state={profile?.state || ''}
                postalCode={profile?.postal_code || ''}
                onAddressChange={e => setProfile({ ...profile, address: e.target.value })}
                onCityChange={e => setProfile({ ...profile, city: e.target.value })}
                onStateChange={value => setProfile({ ...profile, state: value })}
                onPostalCodeChange={e => setProfile({ ...profile, postal_code: e.target.value })}
              />

              <div>
                <Label>What are your favorite meal types?</Label>
                <TagSelector
                  label="Food Types"
                  selectedTags={profile?.food_preferences || []}
                  availableTags={foodTypes}
                  onTagsChange={tags => setProfile({ ...profile, food_preferences: tags })}
                  showSelectAll={true}
                />
              </div>

              <div>
                <Label>What sort of chefs do you prefer to purchase meals from?</Label>
                <TagSelector
                  label="Chef Types"
                  selectedTags={profile?.chef_preferences || []}
                  availableTags={chefTypes}
                  onTagsChange={tags => setProfile({ ...profile, chef_preferences: tags })}
                  showSelectAll={true}
                />
              </div>

              <div>
                <Label>Allergy considerations</Label>
                <TagSelector
                  label="Allergens"
                  selectedTags={profile?.allergy_considerations || []}
                  availableTags={allergens}
                  onTagsChange={tags => setProfile({ ...profile, allergy_considerations: tags })}
                  showNone={true}
                />
              </div>

              <div>
                <Label>Dietary restrictions</Label>
                <TagSelector
                  label="Dietary Restrictions"
                  selectedTags={profile?.dietary_preferences || []}
                  availableTags={dietaryRestrictions}
                  onTagsChange={tags => setProfile({ ...profile, dietary_preferences: tags })}
                  showNone={true}
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <Label className="text-base">Location Services</Label>
                    <p className="text-sm text-muted-foreground">
                      Manage how we use your location to provide better service
                    </p>
                  </div>
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label>Location Permission</Label>
                      <p className="text-muted-foreground text-xs">
                        {getLocationStatusText()}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleLocationUpdate}
                      disabled={locationState.isLoading || isLoading || locationState.permissionTransitioning || !locationState.initialized || isResetting}
                      className="min-w-[120px] whitespace-nowrap ml-4 text-sm"
                    >
                      <LocationButtonState 
                        isLoading={locationState.isLoading || isLoading || locationState.permissionTransitioning}
                        permission={locationState.permission}
                        initialized={locationState.initialized}
                        hasCoords={!!locationState.coords}
                        isResetting={isResetting}
                      />
                    </Button>
                  </div>

                  <div className="flex items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <Label>Reset Location Preferences</Label>
                      <p className="text-muted-foreground text-xs">
                        Clear saved location data and preferences
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleLocationReset}
                      disabled={isLoading || isResetting || !locationState.initialized || locationState.permissionTransitioning}
                      className="min-w-[120px] whitespace-nowrap ml-4 text-sm"
                    >
                      {isResetting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Resetting...
                        </>
                      ) : "Reset"}
                    </Button>
                  </div>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || isResetting || !locationState.initialized}
              >
                {isLoading ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
};

export default AccountSettings;
